package ai.wagemix.web.screens.picker

import ai.wagemix.logic.picker.WMOccupationLimit
import ai.wagemix.logic.picker.WMBaseOccupationPickerPresenter
import ai.wagemix.logic.picker.WMOccupationPickerRow
import ai.wagemix.logic.picker.WMDefaultOccupationPickerPresenter
import ai.wagemix.logic.picker.WMOccupationPickerPresenter
import ai.wagemix.logic.picker.WMOccupationPickerPresenter.Action
import ai.wagemix.logic.picker.WMOccupationPickerPresenter.Event
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.FSWebViewModel
import com.fsryan.foundation.web.screens.FSWebViewStateHolder

interface WMOccupationPickerWebViewStateHolder: FSWebViewStateHolder {
    val rowsState: State<List<WMOccupationPickerRow>>
    val queryState: State<String>
    val selectionCountState: State<Int>
    val taxonomyErrorState: State<Boolean>
    /** The occupation limit when the last add was refused for passing it; `null` once the rows change. */
    val occupationLimitState: State<WMOccupationLimit?>
}

abstract class WMBaseOccupationPickerWebViewModel<
    E: Event,
    A: Action,
    PR: WMOccupationPickerPresenter<E, A>
>(
    presenter: PR,
    protected val onDone: () -> Unit
): FSWebViewModel<E, A, PR>(presenter), WMOccupationPickerWebViewStateHolder {

    override val rowsState: State<List<WMOccupationPickerRow>> get() = _rowsState
    protected val _rowsState = mutableStateOf<List<WMOccupationPickerRow>>(emptyList())

    override val queryState: State<String> get() = _queryState
    protected val _queryState = mutableStateOf("")

    override val selectionCountState: State<Int> get() = _selectionCountState
    protected val _selectionCountState = mutableStateOf(0)

    override val taxonomyErrorState: State<Boolean> get() = _taxonomyErrorState
    protected val _taxonomyErrorState = mutableStateOf(false)

    override val occupationLimitState: State<WMOccupationLimit?> get() = _occupationLimitState
    protected val _occupationLimitState = mutableStateOf<WMOccupationLimit?>(null)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            rowsUpdate?.let {
                _rowsState.value = it
                _occupationLimitState.value = null
            }
            queryUpdate?.let { _queryState.value = it }
            selectionCountUpdate?.let { _selectionCountState.value = it }
            taxonomyErrorUpdate?.let { _taxonomyErrorState.value = it }
            occupationLimitUpdate?.let { _occupationLimitState.value = it }
            doneUpdate?.let { onDone() }
        }
    }

    fun userChangedQuery(query: String) = presenter.userChangedQuery(query)
    fun userToggledExpanded(occupationId: Int) {
        presenter.userToggledExpanded(occupationId, mapOf(WMBaseOccupationPickerPresenter.KEY_QUERY to _queryState.value))
    }
    fun userToggledSelected(occupationId: Int) {
        presenter.userToggledSelected(occupationId, mapOf(WMBaseOccupationPickerPresenter.KEY_QUERY to _queryState.value))
    }
    fun userClearedSelection() = presenter.userClearedSelection(mapOf(WMBaseOccupationPickerPresenter.KEY_QUERY to _queryState.value))
    fun userRequestedAddSelection() = presenter.userRequestedAddSelection()
}

typealias WMDefaultOccupationPickerWebViewModel = WMBaseOccupationPickerWebViewModel<Event.Base, Action.Base, WMDefaultOccupationPickerPresenter>

fun createDefaultOccupationPickerWebViewModel(
    presenter: WMDefaultOccupationPickerPresenter,
    onDone: () -> Unit
): WMDefaultOccupationPickerWebViewModel = object: WMDefaultOccupationPickerWebViewModel(presenter, onDone) {}
